import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { DOMAIN } from "../../api-data/util/constants";
import { FACEBOOK_URL, INSTAGRAM_URL } from "../../utils/socialLinks";

const SCRIPT_ID = "org-json-ld";

interface CityPage {
  data: { city: string }[];
  metaData: { page: number; totalPages: number };
}

interface CategoryPage {
  data: { categoryName: string }[];
}

/** Fetches ALL pages from /public/cities and returns a flat city name array */
async function fetchAllCities(): Promise<string[]> {
  const cities: string[] = [];
  let page = 0;
  let totalPages = 1;
  const base = `${DOMAIN}/api/v1/service-mgmt/service-area/public/cities?size=200&sort=city,asc`;
  while (page < totalPages) {
    const res = await fetch(`${base}&page=${page}`);
    if (!res.ok) break;
    const json: CityPage = await res.json();
    json.data.forEach((c) => cities.push(c.city));
    totalPages = json.metaData.totalPages ?? 1;
    page += 1;
  }
  return cities;
}

/** Fetches the first page of categories (all, sorted) */
async function fetchCategories(): Promise<string[]> {
  const res = await fetch(
    `${DOMAIN}/api/v1/service-mgmt/category/public/all?page=0&size=100&sort=categoryName,asc`
  );
  if (!res.ok) return [];
  const json: CategoryPage = await res.json();
  return json.data.map((c) => c.categoryName);
}

export function OrganizationJsonLd() {
  const { data: cities } = useQuery<string[]>({
    queryKey: ["org-jsonld-cities"],
    queryFn: fetchAllCities,
    staleTime: 60 * 60 * 1000,   // 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: serviceTypes } = useQuery<string[]>({
    queryKey: ["org-jsonld-categories"],
    queryFn: fetchCategories,
    staleTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!cities?.length && !serviceTypes?.length) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "OnlyOneTap",
      url: "https://www.onlyonetap.com",
      logo: {
        "@type": "ImageObject",
        url: "https://www.onlyonetap.com/OnlyOneTapLogo.jpeg",
      },
      image: "https://www.onlyonetap.com/OnlyOneTapLogo.jpeg",
      description:
        "OnlyOneTap connects you with reliable local service providers for cleaning, plumbing, electrician services, and more household needs. Book trusted professionals in just one tap.",
      priceRange: "$$",
      ...(cities?.length && {
        areaServed: cities.map((name) => ({ "@type": "City", name })),
      }),
      ...(serviceTypes?.length && { serviceType: serviceTypes }),
      sameAs: [FACEBOOK_URL, INSTAGRAM_URL],
    };

    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  }, [cities, serviceTypes]);

  return null;
}
